import React from 'react'
import { gql, useMutation } from '@apollo/client'

import { useRouteMatch, Link, useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import { Box, Tooltip, Typography } from '@material-ui/core'
import VisibilityOutlinedIcon from 'components/material-symbol/icons/Visibility'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import { useSnackbar } from 'notistack'
import IconButton from '@material-ui/core/IconButton'
import ActionsMenu from 'components/actions-menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import DeleteOutlineOutlinedIcon from 'components/material-symbol/icons/DeleteOutline'
import StarBorderOutlinedIcon from 'components/material-symbol/icons/StarBorder'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import MoreHorizIcon from 'components/material-symbol/icons/MoreHoriz'
import ListItemText from '@material-ui/core/ListItemText'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Avatar from '@material-ui/core/Avatar'
import { FormattedOneLineAddress } from 'components/util/formatted_address'
import DeleteFacilityDialog from 'pages/pga-coach/facilities/delete-facility-dialog'

const TOGGLE_COACH_PROFILE_FACILITY_VISIBILITY = gql`
  mutation toggleCoachProfileFacilityVisibility(
    $params: ToggleCoachProfileFacilityVisibilityInput!
  ) {
    toggleCoachProfileFacilityVisibility(params: $params) {
      message
      success
    }
  }
`

const SET_PRIMARY_COACH_PROFILE_FACILITY = gql`
  mutation setPrimaryCoachProfileFacility($facilityId: ID!) {
    setPrimaryCoachProfileFacility(facilityId: $facilityId) {
      message
      success
    }
  }
`

const useStyles = makeStyles(theme => ({
  row: {
    cursor: 'pointer',
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  facilityAvatar: {
    height: 36,
    width: 36,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      borderColor: '#C4C7C9',
      color: '#6B7C87',
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    borderRadius: 4,
    color: '#6B7C87',
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    marginRight: theme.spacing(2),
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const FacilityListItem = ({ facility, hasCoachProfile, refetch }) => {
  const styles = useStyles()
  const { path } = useRouteMatch()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()

  const isPrimary = facility.coachProfilesFacilities[0]?.facilityRank === 1
  const isVisible = facility.coachProfilesFacilities[0]?.visible
  const cannotHide = isPrimary && isVisible
  const address = facility.address1 ? (
    <FormattedOneLineAddress
      address1={facility.address1}
      address2={facility.address2}
      city={facility.city}
      state={facility.state}
      zip={facility.zip}
    />
  ) : null

  const [updateVisibility] = useMutation(
    TOGGLE_COACH_PROFILE_FACILITY_VISIBILITY,
  )
  const [updatePrimary] = useMutation(SET_PRIMARY_COACH_PROFILE_FACILITY)

  const handleVisibilityClick = nextVisible => {
    updateVisibility({
      variables: {
        params: {
          facilityId: facility.id,
          visible: nextVisible,
        },
      },
    })
      .then(data => {
        if (!data?.data?.toggleCoachProfileFacilityVisibility?.success) {
          enqueueSnackbar(
            data?.data?.toggleCoachProfileFacilityVisibility?.message ||
              'Something went wrong',
            {
              variant: 'error',
            },
          )
        } else {
          refetch()
          enqueueSnackbar('Successfully updated visibility', {
            variant: 'success',
            preventDuplicate: true,
          })
        }
      })
      .catch(err => {
        enqueueSnackbar(err?.message || 'Something went wrong', {
          variant: 'error',
        })
      })
  }

  const handleSetPrimaryClick = () => {
    updatePrimary({
      variables: {
        facilityId: facility.id,
      },
    })
      .then(data => {
        if (!data?.data?.setPrimaryCoachProfileFacility?.success) {
          enqueueSnackbar(
            data?.data?.setPrimaryCoachProfileFacility?.message ||
              'Something went wrong',
            {
              variant: 'error',
            },
          )
        } else {
          if (!isVisible) {
            // Primary facilities should always be visible
            handleVisibilityClick(true)
          }
          refetch()
          enqueueSnackbar('Successfully updated primary facility', {
            variant: 'success',
          })
        }
      })
      .catch(err => {
        enqueueSnackbar(err?.message || 'Something went wrong', {
          variant: 'error',
        })
      })
  }

  const visibilityTooltip = cannotHide
    ? 'The primary facility cannot be hidden or deleted.'
    : isVisible
      ? 'Visible on Coach Profile'
      : 'Hidden on Coach Profile'

  return (
    <TableRow
      className={styles.row}
      onClick={() => history.push(`${path}/${facility.id}`)}
      data-cy="facilityShowLink"
      data-testid={`facility-list-row-${facility.id}`}
    >
      <TableCell className={styles.info}>
        <Box
          className={styles.nameCell}
          data-testid={`facility-list-name-${facility.id}`}
        >
          <Avatar className={styles.facilityAvatar}>
            <LocationOnOutlinedIcon />
          </Avatar>
          <Box className={styles.info}>
            <Typography className={styles.text} noWrap>
              {facility.name}
            </Typography>
            {address && (
              <Typography className={styles.textSecondary} noWrap>
                {address}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell className={styles.actions} onClick={e => e.stopPropagation()}>
        <Box className={styles.actionButtons}>
          {isPrimary && (
            <Tooltip
              title="The primary facility cannot be hidden or deleted."
              placement="bottom"
            >
              <span className={styles.statusChip}>Primary</span>
            </Tooltip>
          )}
          {hasCoachProfile && (
            <Tooltip title={visibilityTooltip} placement="bottom">
              <span>
                <IconButton
                  aria-label={visibilityTooltip}
                  className={styles.borderedActionButton}
                  disabled={cannotHide}
                  onClick={() => handleVisibilityClick(!isVisible)}
                >
                  {isVisible ? (
                    <VisibilityOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <ActionsMenu
            ariaId={`facility-${facility.id}`}
            aria-label="Facility actions"
            data-testid={`facility-list-actions-menu-${facility.id}`}
            buttonContent={<MoreHorizIcon fontSize="small" />}
            className={styles.plainActionButton}
            color="default"
          >
            <MenuItem
              component={Link}
              to={{ pathname: `${path}/${facility.id}` }}
            >
              <ListItemIcon className={styles.menuIcon}>
                <LocationOnOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Details" />
            </MenuItem>
            <MenuItem
              component={Link}
              className={
                facility.externalFacilityId && (!hasCoachProfile || isPrimary)
                  ? styles.disabled
                  : null
              }
              to={{
                pathname: `${path}/${facility.id}/edit`,
                state: { facility },
              }}
            >
              <ListItemIcon className={styles.menuIcon}>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Edit Details" />
            </MenuItem>
            {hasCoachProfile && (
              <MenuItem
                className={cannotHide ? styles.disabled : null}
                onClick={() => handleVisibilityClick(!isVisible)}
              >
                <ListItemIcon className={styles.menuIcon}>
                  {isVisible ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={isVisible ? 'Hide on Profile' : 'Show on Profile'}
                />
              </MenuItem>
            )}
            {!isPrimary && hasCoachProfile ? (
              <MenuItem onClick={handleSetPrimaryClick}>
                <ListItemIcon className={styles.menuIcon}>
                  <StarBorderOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Set as Primary" />
              </MenuItem>
            ) : null}
            <MenuItem className={isPrimary ? styles.disabled : null}>
              <ListItemIcon className={styles.menuIcon}>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <DeleteFacilityDialog
                facilityId={facility.id}
                onSuccess={() => history.push('/pga-coach/facilities')}
                trigger={<ListItemText primary="Delete Facility" />}
              />
            </MenuItem>
          </ActionsMenu>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default FacilityListItem
